import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { values, keys, orderBy } from 'lodash';

import { scenariosActionTypes } from './../actions/types';
import { dispatchersById } from './dispatchers';
import { cachedRequestByIdReducer } from './helpers';
import { utils } from './../helpers';

const scenarios = cachedRequestByIdReducer(scenariosActionTypes);
export default scenarios;

const scenarioState = state => state.scenarios;
export const scenariosById = state => scenarioState(state).byId;
export const getScenarioById = (state, id) => {
  return scenariosById(state)[id];
};

export const getScenarios = createSelector(
  scenarioState,
  (scenarioState) => values(scenarioState.byId)
);

export const groupScenariosByDispatcherId = createSelector(
  getScenarios,
  (scenarios) => utils.propertyAccumulatorReducer(scenarios, 'dispatcher')
);

/**
 * Keys => lowercase scenario names
 * Values => dispatcher objects that dispatched the scenario
 */
export const groupDispatchersByScenarioName = createSelector(
  groupScenariosByDispatcherId,
  dispatchersById,
  (scenariosByDispatcherId, dispatchersById) => {
    const result = Object.keys(scenariosByDispatcherId).reduce((acc, dispatcherId) => {
      const scenarios = scenariosByDispatcherId[dispatcherId] || [];
      scenarios.forEach(s => {
        const name = s.name.toLowerCase();
        const dispatcher = dispatchersById[dispatcherId];
        acc[name] = [...(acc[name] || []), dispatcher];
      });
      return acc;
    }, {});
    return result;
  }
);

export const getScenariosByDispatcherId = (state, dispatcherId) =>
  groupScenariosByDispatcherId(state)[dispatcherId];

/**
 * Creates a scenario lookup containing only scenarios ran under given dispatcherId
 *
 * Keys => scenario name
 * Values => scenario object
 */
export const getDispatchedScenariosByName = createCachedSelector(
  (state, dispatcherId) => getScenariosByDispatcherId(state, dispatcherId) || [],
  (dispatchedScenarios) => {
    const result = dispatchedScenarios.reduce((acc, scenario) => {
      acc[scenario.name.toLowerCase()] = scenario;
      return acc;
    }, {});
    return result;
  }
)(
  (_state, dispatcherId) => dispatcherId
);

export const groupScenariosByName = createSelector(
  getScenarios,
  (scenarios) => utils.propertyAccumulatorReducer(scenarios, 'name')
);

export const groupScenariosByRunner = createSelector(
  getScenarios,
  (scenarios) => utils.propertyAccumulatorReducer(scenarios, 'runner')
);

const scenariosByNamesOrderByDate = createSelector(
  groupScenariosByName,
  (scenariosByName) => values(scenariosByName).reduce(
    (result, scenarios) => {
      if (scenarios.length === 0) {
        return;
      }

      result[scenarios[0].name] =
          orderBy(scenarios, d => d.created_at, 'desc');
      return result;
    }, {}
  )
);

export const getScenariosByNameOrderByDate = (state, name) =>
  scenariosByNamesOrderByDate(state)[name];

export const checkScenariosDispatcherIds = (state, dispatcherIds) => {
  const fetchedDispatcherIds = keys(groupScenariosByDispatcherId);
  if (dispatcherIds.length !== fetchedDispatcherIds.length) {
    return false;
  }

  for (let i = dispatcherIds.length; i--;) {
    if (dispatcherIds[i] !== fetchedDispatcherIds[i])
      return false;
  }

  return true;
};

/**
 * Get a scenario by the runner job's id.
 * @param {*} state the state
 * @param {*} runner the id of the runner job
 * @returns a scenario object
 */
export const getScenarioByRunner = (state, runner) => {
  const runnerArray = groupScenariosByRunner(state)[runner];
  const scenario = runnerArray && runnerArray[0];
  return scenario;
};


// WEBPACK FOOTER //
// src/reducers/scenarios.js